<template>
  <div
    class="h-screen min-h-screen overflow-y-auto flex flex-col bg-pitch-black text-total-white"
  >
    <!-- Header Section -->
    <div
      class="flex w-full justify-between items-center px-4 p-3 border border-x-0 border-t-0 border-b-1 border-prime-golden bg-pitch-black-900"
    >
      <a href="/" class="text-prime-golden text-base font-medium font-avenir-medium">
        <img
         class="w-24 h-24 -my-8 -ml-[10px]"

          src="@/assets/images/mason-logo.svg"
          alt="Mason Logo"
        />
      </a>
    </div>

    <div class="flex-1 mb-2 overflow-y-auto pb-[100px]">
      <!-- Top Bar -->
      <div class="flex w-full justify-between items-center p-4 bg-pitch-black">
        <div class="flex items-center">
          <button @click="goBack" class="text-prime-golden text-lg mr-4">
            <img
              src="@/assets/images/left-chevron.svg"
              alt="Back"
              width="16"
              height="16"
            />
          </button>
          <h1 class="text-xl font-semibold">Edit Project</h1>
        </div>
        <!-- Delete Project Button for Admin Users -->
        <div class="flex gap-3">
          <div v-if="false">
            <button @click="confirmDelete" class="text-red-500 font-semibold">
              Delete Project
            </button>
          </div>
          <!-- <button @click="cancelEdit" class="text-total-white-500 font-semibold">
            Cancel
          </button> -->
        </div>
      </div>

      <!-- Edit Form Section -->
      <div class="flex-1 overflow-y-auto p-4 pb-[200px]" :class="{ 'opacity-40': isLoading }">
        <form @submit.prevent="saveChanges">
          <div class="max-w-4xl mx-auto">
            <!-- Update Button with Loading State -->
            <div class="pt-4 flex gap-2 justify-end">
              <button
                type="submit"
                class="bg-prime-golden text-pitch-black px-3 py-1 rounded-lg font-semibold text-sm"
                :disabled="isLoading"
              >
                <span v-if="isLoading">Creating...</span>
                <span v-else>Save</span>
              </button>
              <button @click="cancelEdit" class="bg-gray-600 text-total-white px-3 py-1 rounded-lg font-semibold text-sm">
                Cancel
              </button>
            </div>
            <!-- Address Line 1 -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="addressLine1">
                Address Line 1 *
              </label>
              <input
                id="addressLine1"
                v-model="form.addressLine1"
                @input="errors.addressLine1 = ''"
                type="text"
                class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
                placeholder="Enter address"
              />
              <!-- Address Line 1 Error Message -->
              <div v-if="errors.addressLine1" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.addressLine1 }}
              </div>
            </div>

            <!-- Address Line 2 (Non-Required) -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="addressLine2">
                Address Line 2
              </label>
              <input
                id="addressLine2"
                v-model="form.addressLine2"
                type="text"
                class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
                placeholder="Enter address line 2"
              />
              <!-- Address Line 2 Error Message -->
              <!-- This field is non-required, so no error message is needed -->
            </div>

            <!-- Scope -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="scope">
                Scope *
              </label>
              <input
                id="scope"
                v-model="form.scope"
                @input="errors.scope = ''"
                type="text"
                class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
                placeholder="Enter project scope"
              />
              <!-- Scope Error Message -->
              <div v-if="errors.scope" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.scope }}
              </div>
            </div>

            <!-- Start Date -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="startDate">
                Start Date (MM/DD/YYYY) *
              </label>
              <!-- Date Picker Component -->
              <VueDatePicker
                v-model="form.startDate"
                @change="handleDateChange"
                :enable-time-picker="false"
                aria-placeholder="MM/DD/YYYY"
                dark
                :class="[
                  'w-full p-1 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden',
                ]"
              />

              <!-- Start Date Error Message -->
              <div v-if="errors.startDate" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.startDate }}
              </div>
            </div>

            <!-- Country -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="country">
                Country *
              </label>
              <CustomDropdown
                :options="countryOptions"
                v-model="form.country"
                :placeholder="'Select country'"
              />
              <!-- Country Error Message -->
              <div v-if="errors.country" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.country }}
              </div>
            </div>

            <!-- State -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="state">
                State *
              </label>
              <CustomDropdown
                :options="stateOptions"
                v-model="form.state"
                :placeholder="'Select state'"
              />
              <!-- State Error Message -->
              <div v-if="errors.state" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.state }}
              </div>
            </div>

            <!-- City -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="city">
                City *
              </label>
              <CustomDropdown
                :options="cityOptions"
                v-model="form.city"
                :placeholder="'Select city'"
              />
              <!-- City Error Message -->
              <div v-if="errors.city" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.city }}
              </div>
            </div>

            <!-- Zip Code -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="zip">
                Zip Code *
              </label>
              <input
                id="zip"
                v-model="form.zip"
                @input="errors.zip = ''"
                type="text"
                class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:border-prime-golden"
                placeholder="Enter zip code"
              />
              <!-- Zip Code Error Message -->
              <div v-if="errors.zip" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.zip }}
              </div>
            </div>

            <!-- Project Manager Dropdown -->
            <!--<div class="mb-4">
              <label
                class="flex justify-start text-sm font-medium mb-1"
                for="projectManager"
              >
                Project Manager
              </label>
              <CustomDropdown
                v-model="form.projectManagerId"
                :options="contactOptions"
                :placeholder="'Select a project manager'"
                @update:label="handleProjectManagerLabelChange"
              />

              <div
                v-if="errors.projectManagerId"
                class="text-deep-rust text-sm text-left mt-1"
              >
                {{ errors.projectManagerId }}
              </div>
            </div>-->

            <!-- Homeowner Dropdown -->
            <div class="mb-4">
              <label class="flex justify-start text-sm font-medium mb-1" for="homeowner">
                Homeowner
              </label>

              <!-- Homeowner Dropdown -->
              <CustomDropdown
                v-model="form.homeownerId"
                :options="contactOptions"
                :placeholder="'Select a homeowner'"
                @update:label="handleHomeownerLabelChange"
              />

              <!-- Homeowner Error Message -->
              <div v-if="errors.homeownerId" class="text-deep-rust text-sm text-left mt-1">
                {{ errors.homeownerId }}
              </div>
            </div>

            <!-- General Error Message -->
            <div v-if="errorMessage" class="text-deep-rust text-center mt-2">
              {{ errorMessage }}
            </div>

          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { useProjectStore } from "@/stores/project";
import { useUserStore } from "@/stores/user";
import CustomDropdown from "@/components/CustomDropdown.vue";
import { Country, State, City } from "country-state-city";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { useContactsStore } from "@/stores/contact";

// Refs for form, errors, loading state, and messages
const form = ref({
  startDate: "",
  scope: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  simulation: false,
  projectManagerId: "",
  projectManagerLabel: "",
  homeownerId: "",
  homeownerLabel: "",
});

// Dropdown data
const countries = ref(Country.getAllCountries());
const states = ref([]);
const cities = ref([]);

// Error handling
const errors = ref({});
const errorMessage = ref("");
const isLoading = ref(false);

const router = useRouter();
const projectStore = useProjectStore();
const userStore = useUserStore();

const contactsStore = useContactsStore();
const contacts = ref([]);

// Computed property to check if user is admin
onMounted(async () => {
  if (!userStore.profile) {
    try {
      await userStore.fetchUserProfile();
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  }
});
const isAdmin = computed(() => {
  return (
    userStore?.profile?.role == "admin" || userStore?.profile?.account_type == "admin"
  );
});

// Format date to MM/DD/YYYY
const formatDateToMMDDYYYY = (dateString) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Format date to YYYY-MM-DD for saving
const formatDateToYYYYMMDD = (dateString) => {
  const [month, day, year] = dateString.split("/");
  return `${year}-${month}-${day}`;
};

// Handle date picker change
const handleDateChange = (date) => {
  form.value.startDate = formatDateToMMDDYYYY(date);
};
const goToCreateContactPage = () => {
  router.push({ name: "CreateContactPage" });
};

// Watchers
watch(
  () => form.value.country,
  (newCountry) => {
    errors.value.country = "";

    // Update states list for the dropdown
    if (newCountry) {
      const selectedCountry = countries.value.find(
        (country) => country.name === newCountry
      );
      states.value = selectedCountry
        ? State.getStatesOfCountry(selectedCountry.isoCode)
        : [];
    } else {
      states.value = [];
    }
  }
);

watch(
  () => form.value.state,
  (newState) => {
    errors.value.state = "";

    // Update cities list for the dropdown
    if (newState && form.value.country) {
      const selectedCountry = countries.value.find(
        (country) => country.name === form.value.country
      );
      const selectedState = states.value.find((state) => state.name === newState);
      cities.value =
        selectedCountry && selectedState
          ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
          : [];
    } else {
      cities.value = [];
    }
  }
);

// Load project on component mount
const loadProject = async () => {
  try {
    const projectId = router.currentRoute.value.params.id;

    if (!projectStore.currentProject || projectStore.currentProject.id !== projectId) {
      await projectStore.fetchProjectById(projectId);
    }

    const currentProject = projectStore.currentProject;
    if (currentProject) {
      form.value.startDate = currentProject.start_date
        ? formatDateToMMDDYYYY(currentProject.start_date)
        : "";
      form.value.scope = currentProject.scope || "";
      form.value.addressLine1 = currentProject.address_1 || "";
      form.value.addressLine2 = currentProject.address_2 || "";
      form.value.zip = currentProject.zip || "";
      form.value.country = currentProject.country || "";
      form.value.state = currentProject.state || "";
      form.value.city = currentProject.city || "";
      form.value.simulation = currentProject.is_simulation || false;
      form.value.projectManagerId = currentProject.project_manager_id || "";
      form.value.homeownerId = currentProject.homeowner_id || "";

      // Update states list for the dropdown
      if (form.value.country) {
        const selectedCountry = countries.value.find(
          (country) => country.name === form.value.country
        );
        states.value = selectedCountry
          ? State.getStatesOfCountry(selectedCountry.isoCode)
          : [];
      } else {
        states.value = [];
      }

      // Update cities list for the dropdown
      if (form.value.state && form.value.country) {
        const selectedCountry = countries.value.find(
          (country) => country.name === form.value.country
        );
        const selectedState = states.value.find(
          (state) => state.name === form.value.state
        );
        cities.value =
          selectedCountry && selectedState
            ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
            : [];
      } else {
        cities.value = [];
      }
    }
  } catch (error) {
    console.error("Error loading project:", error);
    errorMessage.value = "Failed to load project details.";
  }
};

const countryOptions = computed(() => {
  return countries.value.map((country) => ({
    label: country.name,
    value: country.name,
  }));
});

const handleProjectManagerLabelChange = (label) => {
  form.value.projectManagerLabel = label;
};

const handleHomeownerLabelChange = (label) => {
  form.value.homeownerLabel = label;
};
const stateOptions = computed(() => {
  const options = states.value.map((state) => ({
    label: state.name,
    value: state.name,
  }));
  if (form.value.state && !options.some((option) => option.value === form.value.state)) {
    options.unshift({ label: form.value.state, value: form.value.state });
  }
  return options;
});

const cityOptions = computed(() => {
  const options = cities.value.map((city) => ({
    label: city.name,
    value: city.name,
  }));
  if (form.value.city && !options.some((option) => option.value === form.value.city)) {
    options.unshift({ label: form.value.city, value: form.value.city });
  }
  return options;
});

const contactOptions = computed(() => {
  return [
    {
      label: "+ New Contact",
      value: "new",
      onClick: (e) => {
        e.preventDefault(); // Prevents the dropdown from closing
        goToCreateContactPage(); // Calls the function to navigate
      },
    },
    ...contacts.value.map((contact) => ({
      label: `${contact.first_name} ${contact.last_name}`,
      value: contact.id,
    })),
  ];
});

const cancelEdit = () => {
  router.go(-1);
};

onMounted(async () => {
  // Fetch contacts to populate the dropdowns
  await contactsStore.fetchAllContacts();
  contacts.value = contactsStore.contacts;

  // Fetch user profile if not already loaded
  if (!userStore.profile) {
    await userStore.fetchUserProfile();
  }

  // Load project data
  await loadProject();
});

// Validate form fields
const validateForm = () => {
  errors.value = {};

  // Validate Start Date
  if (!form.value.startDate) {
    errors.value.startDate = "Start date is required.";
  } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(form.value.startDate)) {
    errors.value.startDate = "Please enter a valid date in MM/DD/YYYY format.";
  }

  // Validate Scope
  if (!form.value.scope) {
    errors.value.scope = "Scope is required.";
  }

  // Validate Address Line 1
  if (!form.value.addressLine1) {
    errors.value.addressLine1 = "Address Line 1 is required.";
  }

  // Address Line 2 is optional, no validation needed

  // Validate Country
  if (!form.value.country) {
    errors.value.country = "Country is required.";
  }

  // Validate State
  if (!form.value.state) {
    errors.value.state = "State is required.";
  }

  // Validate City
  if (!form.value.city) {
    errors.value.city = "City is required.";
  }

  // Validate Zip Code
  if (!form.value.zip) {
    errors.value.zip = "Zip code is required.";
  } else if (!/^[a-zA-Z0-9\s-]+$/.test(form.value.zip)) {
    errors.value.zip = "Please enter a valid zip/postal code.";
  }

  // No validation for Next Milestone (read-only)

  // Validate Project Manager
  /*if (!form.value.projectManagerId) {
    errors.value.projectManagerId = "Project manager is required.";
  }*/

  // Validate Homeowner
  /*if (!form.value.homeownerId) {
    errors.value.homeownerId = "Homeowner is required.";
  }*/

  return Object.keys(errors.value).length === 0;
};

// Save changes
const saveChanges = async () => {
  if (!validateForm()) return;

  isLoading.value = true;
  console.log();
  try {
    const projectData = {
      account_id: userStore.profile.id,
      start_date: formatDateToYYYYMMDD(form.value.startDate) + "T00:00:00Z",
      scope: form.value.scope,
      address_1: form.value.addressLine1,
      address_2: form.value.addressLine2,
      city: form.value.city,
      state: form.value.state,
      zip: form.value.zip,
      country: form.value.country,
      // next_milestone is not sent as it's managed by the backend
      is_simulation: form.value.simulation,
      ...(form.value.projectManagerId
        ? { project_manager_id: form.value.projectManagerId }
        : {}),
      ...(form.value.homeownerId ? { homeowner_id: form.value.homeownerId } : {}),
    };

    await projectStore.updateProject(projectStore.currentProject.id, projectData);
    router.push({ name: "ProjectPage", params: { id: projectStore.currentProject.id } });
  } catch (error) {
    console.error("Error updating project:", error);
    errorMessage.value = "Failed to update project.";
  } finally {
    isLoading.value = false;
  }
};

// Confirm Delete Project
const confirmDelete = () => {
  if (
    confirm("Are you sure you want to delete this project? This action cannot be undone.")
  ) {
    deleteProject();
  }
};

// Delete Project
const deleteProject = async () => {
  try {
    await projectStore.deleteProject(projectStore.currentProject.id);
    router.push({ name: "ProjectsPage" });
  } catch (error) {
    console.error("Error deleting project:", error);
    errorMessage.value = "Failed to delete project.";
  }
};

// Go back
const goBack = () => {
  router.push({ name: "ProjectPage", params: { id: projectStore.currentProject.id } });
};
</script>

<style>
input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
}
.dp__input {
  background-color: #2e2e2e !important;
}

.dp__theme_dark {
  --dp-primary-color: #f6c22dcc;
}
</style>
